// src/redux/demandSuggestionsSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  dismissCampaignDemandSuggestion,
  generateCampaignDemandSuggestions,
  selectCampaignDemandSuggestion,
} from "../services/demandSuggestionService";

const normalizeText = (value) => String(value == null ? "" : value).trim();

const readErrorMessage = (error, fallback) => {
  const message =
    error?.payload?.error?.message ||
    error?.payload?.message ||
    error?.message ||
    fallback;

  return normalizeText(message) || fallback;
};

const normalizeSuggestion = (suggestion) => {
  if (!suggestion || typeof suggestion !== "object") return null;

  const id = normalizeText(suggestion.id);

  if (!id) return null;

  return {
    id,
    campaignTitle: normalizeText(suggestion.campaignTitle),
    campaignDescription: normalizeText(suggestion.campaignDescription),
    offerIdea: normalizeText(suggestion.offerIdea),
    creatorType: normalizeText(suggestion.creatorType),
    localAngle: normalizeText(suggestion.localAngle),
    targetRadiusMiles: Number.isFinite(Number(suggestion.targetRadiusMiles))
      ? Number(suggestion.targetRadiusMiles)
      : 5,
    confidence: normalizeText(suggestion.confidence) || "low",
  };
};

const normalizeSuggestionsResponse = (response) => {
  const raw =
    response?.suggestions ||
    response?.items ||
    response?.data?.suggestions ||
    response?.data?.items ||
    [];

  return Array.isArray(raw) ? raw.map(normalizeSuggestion).filter(Boolean) : [];
};

/**
 * POST /campaigns/demand-suggestions
 */
export const generateCampaignDemandSuggestionsAsync = createAsyncThunk(
  "demandSuggestions/generateCampaignDemandSuggestions",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await generateCampaignDemandSuggestions(payload);

      return {
        suggestions: normalizeSuggestionsResponse(response),
        raw: response,
      };
    } catch (error) {
      return rejectWithValue({
        message: readErrorMessage(
          error,
          "Unable to generate demand suggestions. Please try again."
        ),
        status: error?.status || 0,
        code: error?.code || "",
      });
    }
  }
);

/**
 * POST /campaigns/demand-suggestions/{suggestionId}/select
 */
export const selectCampaignDemandSuggestionAsync = createAsyncThunk(
  "demandSuggestions/selectCampaignDemandSuggestion",
  async ({ suggestionId, campaignId } = {}, { rejectWithValue }) => {
    try {
      const response = await selectCampaignDemandSuggestion(suggestionId, {
        campaignId,
      });

      return {
        suggestionId: normalizeText(suggestionId),
        campaignId: normalizeText(campaignId),
        raw: response,
      };
    } catch (error) {
      return rejectWithValue({
        suggestionId: normalizeText(suggestionId),
        message: readErrorMessage(
          error,
          "Unable to select demand suggestion. Please try again."
        ),
        status: error?.status || 0,
        code: error?.code || "",
      });
    }
  }
);

/**
 * POST /campaigns/demand-suggestions/{suggestionId}/dismiss
 */
export const dismissCampaignDemandSuggestionAsync = createAsyncThunk(
  "demandSuggestions/dismissCampaignDemandSuggestion",
  async ({ suggestionId } = {}, { rejectWithValue }) => {
    try {
      const response = await dismissCampaignDemandSuggestion(suggestionId);

      return {
        suggestionId: normalizeText(suggestionId),
        raw: response,
      };
    } catch (error) {
      return rejectWithValue({
        suggestionId: normalizeText(suggestionId),
        message: readErrorMessage(
          error,
          "Unable to dismiss demand suggestion. Please try again."
        ),
        status: error?.status || 0,
        code: error?.code || "",
      });
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: "",

  selectedSuggestionId: "",
  selectingId: "",
  selectError: "",

  dismissingIds: [],
  dismissError: "",

  lastGeneratedAt: null,
  lastRawResponse: null,
};

const demandSuggestionsSlice = createSlice({
  name: "demandSuggestions",
  initialState,
  reducers: {
    clearDemandSuggestions: () => initialState,

    clearDemandSuggestionErrors: (state) => {
      state.error = "";
      state.selectError = "";
      state.dismissError = "";
    },

    setSelectedDemandSuggestionId: (state, action) => {
      state.selectedSuggestionId = normalizeText(action.payload);
    },

    removeDemandSuggestionLocal: (state, action) => {
      const suggestionId = normalizeText(action.payload);

      if (!suggestionId) return;

      state.items = state.items.filter((item) => item.id !== suggestionId);

      if (state.selectedSuggestionId === suggestionId) {
        state.selectedSuggestionId = "";
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateCampaignDemandSuggestionsAsync.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.lastRawResponse = null;
      })
      .addCase(
        generateCampaignDemandSuggestionsAsync.fulfilled,
        (state, action) => {
          state.loading = false;
          state.error = "";
          state.items = action.payload?.suggestions || [];
          state.lastGeneratedAt = new Date().toISOString();
          state.lastRawResponse = action.payload?.raw || null;
        }
      )
      .addCase(
        generateCampaignDemandSuggestionsAsync.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload?.message ||
            "Unable to generate demand suggestions. Please try again.";
        }
      )

      .addCase(selectCampaignDemandSuggestionAsync.pending, (state, action) => {
        state.selectingId = normalizeText(action.meta?.arg?.suggestionId);
        state.selectError = "";
      })
      .addCase(selectCampaignDemandSuggestionAsync.fulfilled, (state, action) => {
        const suggestionId = normalizeText(action.payload?.suggestionId);

        state.selectingId = "";
        state.selectError = "";

        if (suggestionId) {
          state.selectedSuggestionId = suggestionId;
        }
      })
      .addCase(selectCampaignDemandSuggestionAsync.rejected, (state, action) => {
        state.selectingId = "";
        state.selectError =
          action.payload?.message ||
          "Unable to select demand suggestion. Please try again.";
      })

      .addCase(dismissCampaignDemandSuggestionAsync.pending, (state, action) => {
        const suggestionId = normalizeText(action.meta?.arg?.suggestionId);

        state.dismissError = "";

        if (suggestionId && !state.dismissingIds.includes(suggestionId)) {
          state.dismissingIds.push(suggestionId);
        }
      })
      .addCase(
        dismissCampaignDemandSuggestionAsync.fulfilled,
        (state, action) => {
          const suggestionId = normalizeText(action.payload?.suggestionId);

          state.dismissError = "";
          state.dismissingIds = state.dismissingIds.filter(
            (id) => id !== suggestionId
          );

          if (suggestionId) {
            state.items = state.items.filter((item) => item.id !== suggestionId);
          }

          if (state.selectedSuggestionId === suggestionId) {
            state.selectedSuggestionId = "";
          }
        }
      )
      .addCase(dismissCampaignDemandSuggestionAsync.rejected, (state, action) => {
        const suggestionId = normalizeText(action.payload?.suggestionId);

        state.dismissingIds = state.dismissingIds.filter(
          (id) => id !== suggestionId
        );

        state.dismissError =
          action.payload?.message ||
          "Unable to dismiss demand suggestion. Please try again.";
      });
  },
});

export const {
  clearDemandSuggestions,
  clearDemandSuggestionErrors,
  setSelectedDemandSuggestionId,
  removeDemandSuggestionLocal,
} = demandSuggestionsSlice.actions;

export const selectDemandSuggestionItems = (state) =>
  state.demandSuggestions?.items || [];

export const selectDemandSuggestionLoading = (state) =>
  Boolean(state.demandSuggestions?.loading);

export const selectDemandSuggestionError = (state) =>
  state.demandSuggestions?.error || "";

export const selectSelectedDemandSuggestionId = (state) =>
  state.demandSuggestions?.selectedSuggestionId || "";

export const selectDemandSuggestionSelectingId = (state) =>
  state.demandSuggestions?.selectingId || "";

export const selectDemandSuggestionSelectError = (state) =>
  state.demandSuggestions?.selectError || "";

export const selectDemandSuggestionDismissingIds = (state) =>
  state.demandSuggestions?.dismissingIds || [];

export const selectDemandSuggestionDismissError = (state) =>
  state.demandSuggestions?.dismissError || "";

export const selectDemandSuggestionLastGeneratedAt = (state) =>
  state.demandSuggestions?.lastGeneratedAt || null;

export default demandSuggestionsSlice.reducer;